import { Job } from '../interfaces/job';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Stop } from '../interfaces/stop';
import { DistancePricing } from '../interfaces/distancePricing';
import { LastLocation } from '../interfaces/lastLocation';
import { DriverEvent } from '../interfaces/driverEvent';
import { ToastersService } from './toasters.service';
import { HelperFunctions } from '../../utils/helper.functions';
import { TopicService } from './topic.service';
import { AuthService } from './auth.service';
import { Status } from '../enums/Status';
import { GetJobsRequestOptions } from '../types/GetJobsRequestOptions';
import { TenantFile } from '../interfaces/tenantFile';
import { ContractJob } from '../interfaces/contractJob';
import { DriverLocationOrigin } from '../enums/DriverLocationOrigin';
import { JobDocumentFilterStates } from '../enums/jobDocumentFilterStates';
import { Currency } from '../enums/Currency';
import { Warning } from '../enums/Warning';
import { JobCancellationReason } from '../types/JobCancellationReason';
import { TenantType } from '../enums/TenantType';
import { Crew } from '../types/Crew';
import { ContractJobShareTypeV2 } from '../enums/ContractJobShareType';
import { ShareJobOptions } from '../types/ShareJobOptions';
import { StockKeepingUnitQuantityList } from '../types/SKU';
import { ContractJobMasterFilter } from '../types/ContractJobMasterTypes';
import { objectToQueryParams } from '../../utils/query.functions';
import { ContractJobMasterForHaulier } from '../dto/contract-job-master-for-haulier';
import { ContractJobChangesRequest } from '../types/ContractJobChangesRequest';
import { PickupInterval } from '../types/PickupInterval';
import { HqStop } from '../interfaces/hqStop';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  prefix = `${environment.serverUrl}/jobs`;

  constructor(
    private http: HttpClient,
    private toasterService: ToastersService,
    private helperFunctions: HelperFunctions,
    private topicService: TopicService,
    private authService: AuthService,
  ) {}

  mapOptionsToParams(options: GetJobsRequestOptions) {
    // TODO add more filters fields when changed /job/find to /contact-jobs
    const {
      currentPage,
      perPage,
      origin,
      status,
      search,
      onlyMyJobs,
      sortColumn,
      sortOrder,
      fromDate,
      toDate,
      documentsUploaded,
      podVerified,
      justActiveAndFinished,
      handleArchivedJobs,
    } = options;
    const queryParams = [`currentPage=${currentPage}`, `perPage=${perPage}`];
    if (origin) queryParams.push(`origin=${origin}`);
    if (status) queryParams.push(`status=${status}`);
    if (handleArchivedJobs) queryParams.push(`handleArchivedJobs=${handleArchivedJobs}`);
    if (search) queryParams.push(`search=${encodeURIComponent(search)}`);
    if (sortColumn) queryParams.push(`sortColumn=${sortColumn}`);
    if (sortOrder) queryParams.push(`sortOrder=${sortOrder}`);
    if (onlyMyJobs) queryParams.push(`onlyMyJobs=${onlyMyJobs}`);
    if (fromDate) queryParams.push(`fromDate=${fromDate}`);
    if (toDate) queryParams.push(`toDate=${toDate}`);
    if (documentsUploaded === JobDocumentFilterStates.have || documentsUploaded === JobDocumentFilterStates.dontHave)
      queryParams.push(`documentsUploaded=${Boolean(Number(documentsUploaded))}`);
    if (podVerified === JobDocumentFilterStates.have || podVerified === JobDocumentFilterStates.dontHave)
      queryParams.push(`podVerified=${Boolean(Number(podVerified))}`);
    if (justActiveAndFinished === true) queryParams.push(`justActiveAndFinished=true`);

    return queryParams;
  }

  findJobs(options: GetJobsRequestOptions) {
    return this.http.get<Job[]>(`${this.prefix}/find?${this.mapOptionsToParams(options).join('&')}`).toPromise();
  }

  sendExportedJobsAndFilters(options: GetJobsRequestOptions) {
    return this.http.get<Job[]>(`${this.prefix}/find/export?${this.mapOptionsToParams(options).join('&')}`).toPromise();
  }

  getCreatedJobsPaginated(options: GetJobsRequestOptions) {
    const { currentPage, perPage, origin, status, fromDate, toDate } = options;
    const queryParams = [`currentPage=${currentPage}`, `perPage=${perPage}`];
    if (fromDate) queryParams.push(`fromDate=${fromDate.toISOString()}`);
    if (toDate) queryParams.push(`toDate=${toDate.toISOString()}`);
    if (origin) queryParams.push(`origin=${origin}`);
    if (status) queryParams.push(`status=${status}`);
    return this.http.get<Job[]>(`${this.prefix}/createdByMe?${queryParams.join('&')}`).toPromise();
  }

  getContractJobMasters(options: ContractJobMasterFilter) {
    return this.http
      .get<ContractJobMasterForHaulier[]>(`${this.prefix}/contract-job-masters?${objectToQueryParams(options)}`)
      .toPromise();
  }

  getJob(jobId?: string, withEstimatedTime?: boolean): Promise<Job> {
    // TODO: ZS-504 remove vehicleRequirements mapping
    const queryParams = [`skipVehicleRequirementsMappingToString=true`];
    if (withEstimatedTime) {
      queryParams.push(`withEstimatedTime=${withEstimatedTime}`);
    }
    return this.http
      .get<Job>(`${this.prefix}/${jobId}${queryParams.length > 0 ? `?${queryParams.join('&')}` : ``}`)
      .toPromise();
  }

  saveJob(job: Job): Promise<Job> {
    return this.http.post<Job>(this.prefix, job).toPromise();
  }

  // TODO deep dive into methods and remove postContractJob on save/book create screen and popups
  // TODO This is share to marketplace/circles
  saveAndShareJob(
    job: Partial<Job>,
    shareOptions?: {
      type: ContractJobShareTypeV2;
      options: ShareJobOptions;
    },
    stockKeepingUnitQuantityLists?: Partial<StockKeepingUnitQuantityList[]>,
    haulierPrice?: { price: number; currency: Currency },
  ) {
    return this.http
      .post<{ contractJob: ContractJob }>(`${this.prefix}/save`, {
        job,
        shareOptions,
        stockKeepingUnitQuantityLists,
        haulierPrice,
      })
      .toPromise();
  }

  shareJob(
    jobId: string,
    shareOptions: { type: ContractJobShareTypeV2; options: ShareJobOptions },
    stockKeepingUnitQuantityLists?: Partial<StockKeepingUnitQuantityList[]>,
  ) {
    return this.http
      .post<{ contractJob: ContractJob }>(`${this.prefix}/${jobId}/share`, {
        shareOptions,
        stockKeepingUnitQuantityLists,
      })
      .toPromise();
  }

  saveJobList(jobs: Job[]) {
    return this.http.post(`${this.prefix}/batch`, jobs).toPromise();
  }

  editJob(
    jobId: string,
    job: Job,
    stockKeepingUnitQuantityLists?: Partial<StockKeepingUnitQuantityList[]>,
  ): Promise<Job> {
    return this.http
      .put<Job>(`${this.prefix}/${jobId}`, { job, stockKeepingUnitQuantityLists })
      .toPromise();
  }

  editDraftJob(
    jobId: string,
    job: Job,
    haulierPrice: number,
    haulierCurrency: Currency,
    stockKeepingUnitQuantityLists?: StockKeepingUnitQuantityList[],
  ): Promise<Job> {
    return this.http
      .put<Job>(`${this.prefix}/${jobId}/draft`, {
        job,
        haulierPrice,
        haulierCurrency,
        stockKeepingUnitQuantityLists,
      })
      .toPromise();
  }

  changeJobStatus(jobId: string, status: Status): Promise<any> {
    return this.http.put(`${this.prefix}/${jobId}/status`, { status }).toPromise();
  }

  archiveJob(jobId: string): Promise<Job> {
    return this.http.put<Job>(`${this.prefix}/${jobId}/archive`, {}).toPromise();
  }

  unArchiveJob(jobId: string): Promise<Job> {
    return this.http.delete<Job>(`${this.prefix}/${jobId}/archive`).toPromise();
  }

  setActualTime(
    jobId: string,
    stopId: string,
    actualTime: { actualStartDate?: Date; actualEndDate?: Date },
  ): Promise<any> {
    return this.http
      .put(`${this.prefix}/${jobId}/stops/${stopId}/actualTime`, {
        actualTime,
        driverLocationOrigin: DriverLocationOrigin.web,
      })
      .toPromise();
  }

  deleteJob(jobId: string) {
    return this.http.delete(`${this.prefix}/${jobId}`).toPromise();
  }

  addStop(jobId: string, stop: Stop): Promise<Stop> {
    return this.http.post<Stop>(`${this.prefix}/${jobId}/stops`, stop).toPromise();
  }

  getJobsOnMarketplace(): Promise<Job[]> {
    return this.http.get<Job[]>(`${this.prefix}/marketplace`).toPromise();
  }

  // TODO: Deprecated, use MarketplaceService.publish
  publish(jobId: string, status: string, origin: string): Promise<Job> {
    return this.http
      .put<Job>(`${this.prefix}/publish`, { jobId, status, origin })
      .toPromise();
  }
  changeJobOrigin(jobId: string, origin: string): Promise<Job> {
    return this.http
      .put<Job>(`${this.prefix}/${jobId}/origin`, { origin })
      .toPromise();
  }
  async assignDrivers(jobId: string, stopId: string, crews: Crew[]) {
    return this.http.put(`${this.prefix}/${jobId}/stops/${stopId}/assign`, { crews }).toPromise();
  }

  async canAssignDrivers(jobId: string): Promise<{ allowed: boolean }> {
    return this.http.get<{ allowed: boolean }>(`${this.prefix}/${jobId}/assign-allowed`).toPromise();
  }

  async notifyDrivers(jobId: string) {
    return this.http.get(`${this.prefix}/${jobId}/notify-drivers`).toPromise();
  }

  async uploadFiles(jobId, stopId, file): Promise<any> {
    return this.http.post(`${this.prefix}/${jobId}/stops/${stopId}/files`, file).toPromise();
  }

  async deleteFile(jobId, stopId, fileId): Promise<unknown> {
    return this.http.delete(`${this.prefix}/${jobId}/stops/${stopId}/files/${fileId}`).toPromise();
  }

  async import(jobImporterList): Promise<any> {
    return this.http.post(`${this.prefix}/importer`, jobImporterList).toPromise();
  }

  async checkIfTruckCanMakeIt(jobId, truckId): Promise<any> {
    return this.http.get(`${this.prefix}/${jobId}/truck/${truckId}/ontime`).toPromise();
  }
  async getJobOfferCount(jobId): Promise<any> {
    return this.http.get(`${this.prefix}/${jobId}/offerCount`).toPromise();
  }

  async getPricing(jobId): Promise<DistancePricing> {
    return this.http.get<DistancePricing>(`${this.prefix}/${jobId}/pricing`).toPromise();
  }

  async getLocations(jobId: string, locationsOrigin: DriverLocationOrigin): Promise<LastLocation[][]> {
    const queryParams = locationsOrigin ? `?driverLocationOrigin=${locationsOrigin}` : null;
    return await this.http.get<LastLocation[][]>(`${this.prefix}/${jobId}/locations${queryParams ?? ''}`).toPromise();
  }
  async getDriverEvents(jobId: string): Promise<DriverEvent[]> {
    return await this.http.get<DriverEvent[]>(`${this.prefix}/${jobId}/driverEvents`).toPromise();
  }
  async getAssignedJobsForTruck(truckId: string): Promise<Job[]> {
    return this.http.get<Job[]>(`${this.prefix}/truck/${truckId}`).toPromise();
  }
  async getAssignedJobsForDriver(driverId: string): Promise<Job[]> {
    return this.http.get<Job[]>(`${this.prefix}/driver/${driverId}`).toPromise();
  }
  async getContractForJob(jobId: string): Promise<ContractJob> {
    return this.http.get<ContractJob>(`${this.prefix}/${jobId}/contract`).toPromise();
  }

  async subscribeToJobNotifications() {
    const tenant = this.authService.getTenant();
    const topic = `jobs-${tenant.id}`;
    await this.topicService.subscribeToTopic(topic, () => {});
  }

  async unsubscribeFromJobNotifications() {
    const tenant = this.authService.getTenant();
    const topic = `jobs-${tenant.id}`;
    await this.topicService.unsubscribeFromTopics([topic]);
  }

  async shareJobAsContract(
    jobId: string,
    haulierId: string,
    haulierPrice: number,
    shipperPrice: number,
    shipperCurrency: string,
    penalty: string,
  ): Promise<Job> {
    return this.http
      .post<any>(`${this.prefix}/contractJob/${jobId}`, {
        haulierId,
        haulierPrice,
        shipperPrice,
        shipperCurrency,
        penalty,
      })
      .toPromise();
  }

  async shareSubConJobAsContract(job: Job, haulierId: string, price: number, penalty: string): Promise<Job> {
    return this.http
      .post<any>(`${this.prefix}/contractJob/subcon`, {
        job,
        haulierId,
        price,
        penalty,
      })
      .toPromise();
  }

  async updateContractForJob(
    jobId: string,
    contract: { contract: Partial<ContractJob>; penalty: string; wasHaulierChanged?: boolean },
  ): Promise<unknown> {
    return this.http.put<unknown>(`${this.prefix}/contractJob/${jobId}`, contract).toPromise();
  }
  async updateContractForSubConJob(
    jobId: string,
    haulierId?: string,
    price?: number,
    currency?: Currency,
    penalty?: string,
  ): Promise<unknown> {
    return this.http
      .put<unknown>(`${this.prefix}/contractJob/${jobId}/subcon`, { haulierId, price, currency, penalty })
      .toPromise();
  }
  async cancelContractForHaulier(jobId: string): Promise<{ success: boolean }> {
    return this.http.delete<{ success: true }>(`${this.prefix}/contractJob/${jobId}/haulier`).toPromise();
  }

  async cancelJobWithReason(
    jobId: string,
    type: TenantType,
    cancellationReason: JobCancellationReason,
  ): Promise<{ success: boolean }> {
    return this.http
      .put<{ success: true }>(
        `${this.prefix}/contract-job-masters/${jobId}/tenant-types/${type}/cancel`,
        cancellationReason,
      )
      .toPromise();
  }

  async uploadJobCollectionNote(formData: FormData, jobId: string): Promise<TenantFile> {
    return this.http.put<TenantFile>(`${this.prefix}/${jobId}/collection-note`, formData).toPromise();
  }
  async verifyDocuments(jobId: string, files: { id: string; isChecked: boolean }[], podEmail: string): Promise<Stop[]> {
    return this.http
      .post<Stop[]>(`${this.prefix}/${jobId}/documents`, { files, podEmail })
      .toPromise();
  }

  async addWarning(jobId: string, warning: Warning): Promise<Job> {
    return this.http
      .post<Job>(`${this.prefix}/${jobId}/warning`, { warning })
      .toPromise();
  }

  async flagAsPriority(jobId: string, priority: boolean): Promise<Job> {
    return this.http
      .post<Job>(`${this.prefix}/${jobId}/priority`, { priority })
      .toPromise();
  }

  async shareDraftJobV2(
    jobId: string,
    shareOptions: { type: ContractJobShareTypeV2; options: ShareJobOptions },
  ): Promise<{ contractJob: ContractJob }> {
    return this.http.post<{ contractJob: ContractJob }>(`${this.prefix}/${jobId}/share`, shareOptions).toPromise();
  }

  async changeStatus(jobId: string, status: string): Promise<Job> {
    return this.http
      .put<Job>(`${this.prefix}/${jobId}/status`, { status })
      .toPromise();
  }

  async getHaulierAssignedTrailersPerStop(haulierId: string, jobId: string): Promise<any> {
    return this.http.get(`${this.prefix}/${jobId}/haulier/${haulierId}/assigned-vehicles`).toPromise();
  }

  async findContractJobMasterForHaulier(
    jobId: string,
    withEstimatedTime?: boolean,
  ): Promise<ContractJobMasterForHaulier> {
    return await this.http
      .get<ContractJobMasterForHaulier>(
        `${this.prefix}/contract-job-masters/${jobId}?${objectToQueryParams({ withEstimatedTime })}`,
      )
      .toPromise();
  }
  async acceptLoadRequest(contractJobMasterId: string): Promise<ContractJobMasterForHaulier> {
    return this.http
      .put<ContractJobMasterForHaulier>(`${this.prefix}/contract-job-masters/${contractJobMasterId}/accept`, {})
      .toPromise();
  }

  async rejectLoadRequest(contractJobMasterId: string): Promise<ContractJobMasterForHaulier> {
    return this.http
      .put<ContractJobMasterForHaulier>(`${this.prefix}/contract-job-masters/${contractJobMasterId}/reject`, {})
      .toPromise();
  }

  async assignDriverToContractJobMaster(contractJobMasterId: string, crews: Crew[]) {
    return this.http
      .post<ContractJobMasterForHaulier>(`${this.prefix}/contract-job-masters/${contractJobMasterId}/drivers`, {
        crews,
      })
      .toPromise();
  }

  async getPickupInterval(contractJobMasterId: string) {
    return this.http
      .get<PickupInterval>(
        `${this.prefix}/contract-job-masters/${contractJobMasterId}/load-request/changes/pickup-interval`,
      )
      .toPromise();
  }

  async haulierRequestChangesForContractJobMaster(changes: ContractJobChangesRequest) {
    const payload = {
      pickupDateRequest: changes.pickupDateRequest,
      vehicleRequirements: changes.vehicleRequirements,
    };
    return this.http
      .post<ContractJobMasterForHaulier>(
        `${this.prefix}/contract-job-masters/${changes.jobId}/load-request/changes`,
        payload,
      )
      .toPromise();
  }

  async uploadPodToContractJob(contractJobId: string, formData: FormData) {
    return this.http.post<TenantFile>(`${this.prefix}/contractJob/${contractJobId}/pod-photo`, formData).toPromise();
  }

  async getPodForContractJob(contractJobId: string) {
    return this.http.get<TenantFile[]>(`${this.prefix}/contractJob/${contractJobId}/pod-photo`).toPromise();
  }

  async updateOrders(contractJobId: string, stops: HqStop[]): Promise<ContractJobMasterForHaulier> {
    return this.http
      .put<ContractJobMasterForHaulier>(`${this.prefix}/contract-job-masters/${contractJobId}/orders`, { stops })
      .toPromise();
  }
}
